import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { roleGuard } from './shared/services/guard/role.guard';
import { ModuleName } from './shared/services/module.enum';
import { AnalyticsRoutingModule } from './modules/admin/analytics/analytics-routing.module';
import { FinancesModule } from './modules/admin/finances/finances.module';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    // Redirect empty path to '/example'
    { path: '', pathMatch: 'full', redirectTo: 'users' },

    // Redirect signed-in user to the '/example'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    { path: 'users', pathMatch: 'full', redirectTo: 'users' },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'confirmation-required',
                loadChildren: () =>
                    import(
                        'app/modules/auth/confirmation-required/confirmation-required.routes'
                    ),
            },
            {
                path: 'forgot-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/forgot-password/forgot-password.routes'
                    ),
            },
            {
                path: 'reset-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/reset-password/reset-password.routes'
                    ),
            },
            {
                path: 'sign-in',
                loadChildren: () =>
                    import('app/modules/auth/sign-in/sign-in.routes'),
            },
            {
                path: 'sign-up',
                loadChildren: () =>
                    import('app/modules/auth/sign-up/sign-up.routes'),
            },


        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: () =>
                    import('app/modules/auth/sign-out/sign-out.routes'),
            },
            {
                path: 'unlock-session',
                loadChildren: () =>
                    import(
                        'app/modules/auth/unlock-session/unlock-session.routes'
                    ),
            },
        ],
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'home',
                loadChildren: () =>
                    import('app/modules/landing/home/home.routes'),
            },
        ],
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'example',
                loadChildren: () =>
                    import('app/modules/admin/example/example.routes'),
            },
            {
                path: '404',
                loadChildren: () =>
                    import('app/modules/admin/page-not-found/page-not-found.routes'),
            },
            // ticket-booking TicketBookingModule
            {
                path: 'ticket-booking',
                loadChildren: () =>
                    import(
                        './modules/admin/ticket-booking/ticket-booking.module'
                    ).then((m) => m.TicketBookingModule),
                canActivate: [roleGuard],
                data: { moduleName: ModuleName.TICKET_BOOKING, actionName: 'view' },
            },
            // {
            //     path: 'users',
            //     loadChildren: () =>
            //         import('app/modules/admin/contacts/contacts.routes'),
            // },
            {
                path: 'cms',
                loadChildren: () =>
                    import('app/modules/admin/tasks/tasks.routes'),
                canActivate: [roleGuard],
                data: { moduleName: ModuleName.Website, actionName: 'view' },
            },
            {
                path: 'packages',
                loadChildren: () =>
                    import('app/modules/admin/packages/packages.routes'),
                canActivate: [roleGuard],
                data: { moduleName: ModuleName.Package, actionName: 'view' },
            },
            {
                path: 'shifts',
                loadChildren: () =>
                    import('app/modules/admin/shifts/shifts.routes'),
                canActivate: [roleGuard],
                data: { moduleName: ModuleName.Shifts, actionName: 'view' },
            },
            {
                path: 'ticket',
                loadChildren: () =>
                    import(
                        './modules/admin/ticket-schedule/ticket-schedule.module'
                    ).then((m) => m.TicketScheduleModule),
                canActivate: [roleGuard],
                data: { moduleName: ModuleName.TicketSchedules, actionName: 'view' },
            },
            {
                path: 'seat-layout',
                loadChildren: () =>
                    import(
                        './modules/admin/seat-layout/seat-layout.module'
                    ).then((m) => m.SeatLayoutModule),
                canActivate: [roleGuard],
                data: { moduleName: ModuleName.SEAT_LAYOUT, actionName: 'view' },
            },
            {
                path: 'order',
                loadChildren: () =>
                    import('./modules/admin/order/order.module').then(
                        (m) => m.OrderModule
                    ),
                canActivate: [roleGuard],
                data: { moduleName: ModuleName.TICKET_ORDER, actionName: 'view' },
            },
            {
                path: 'order-list',
                loadChildren: () =>
                    import('./modules/admin/order-list/order-list.module').then(
                        (m) => m.OrderListModule
                    ),
                canActivate: [roleGuard],
                data: { moduleName: ModuleName.OrderList, actionName: 'view' },
            },
            {
                path: 'customer-dashboard',
                loadChildren: () =>
                    import(
                        './modules/admin/customer-dashboard/customer-dashboard.module'
                    ).then((m) => m.CustomerDashboardModule),
                canActivate: [roleGuard],
                data: { moduleName: ModuleName.CUSTOMER_DASHBOARD, actionName: 'view' },
            },

            {
                path: 'vehicles',
                loadChildren: () =>
                    import('app/modules/admin/transport/transport.routes'),
                canActivate: [roleGuard],
                data: { moduleName: ModuleName.Vehicles, actionName: 'view' },
            },
            {
                path: 'area-pricing',
                loadChildren: () =>
                    import(
                        'app/modules/admin/area-pricing/area-pricing.routes'
                    ),
                canActivate: [roleGuard],
                data: { moduleName: ModuleName.AreaPricing, actionName: 'view' },
            },
            {
                path: 'floor', loadComponent: () => import('app/modules/admin/floor/floor.component').then(m => m.FloorComponent),
                canActivate: [roleGuard],
                data: { moduleName: ModuleName.Floors, actionName: 'view' },
            },
            {
                path: 'ground-floor', loadComponent: () => import('app/modules/admin/floor/ground-floor/ground-floor.component').then(m => m.GroundFloorComponent),
                canActivate: [roleGuard],
                data: { moduleName: ModuleName.Floors, actionName: 'view' },

            },
            {
                path: 'roles', loadComponent: () => import('app/modules/admin/roles/roles.component').then(m => m.RolesComponent),
                // canActivate: [roleGuard],
                data: { moduleName: ModuleName.Roles, actionName: 'view' },

            },
            {
                path: 'users', loadComponent: () => import('app/modules/admin/users/users.component').then(m => m.UsersComponent),
                canActivate: [roleGuard],
                data: { moduleName: ModuleName.Users, actionName: 'view' },
            },
            {
                path: 'transactions', loadComponent: () => import('app/modules/admin/transactions/transactions.component').then(m => m.TransactionsComponent),
                canActivate: [roleGuard],
                data: { moduleName: ModuleName.Transactions, actionName: 'view' },
            },
            {
                path: 'profile', loadComponent: () => import('app/modules/admin/profile/profile.component').then(m => m.ProfileComponent),
            },
            {
                path: 'customers', loadComponent: () => import('app/modules/admin/customers/customers.component').then(m => m.CustomersComponent),

                canActivate: [roleGuard],
                data: { moduleName: ModuleName.Customers, actionName: 'view' },
            },
            {
                path: 'inventory',
                loadChildren: () => import('app/modules/admin/inventory/inventory.routes'),
                // canActivate: [roleGuard],
                data: { moduleName: ModuleName.Inventory, actionName: 'view' },
            },
            {
                path: 'food-booking',
                loadChildren: () => import('app/modules/admin/food-order/food-order.module').then(m => m.FoodOrderModule),
                // canActivate: [roleGuard],
            },
            {
                path: 'analytics-dashboard',
                loadChildren: () => import('app/modules/admin/analytics/analytics.module').then(m => m.AnalyticsModule),
                // canActivate: [roleGuard],
            },
            {
                path: 'finances-dashboard',
                loadChildren: () => import('app/modules/admin/finances/finances.module').then(m => m.FinancesModule),
                // canActivate: [roleGuard],
            },
            {
                path: 'food-orders',
                loadComponent: () => import('app/modules/admin/food-order-list/list/list.component').then(m => m.ListComponent),
                // canActivate: [roleGuard],
            },
            {
                path: 'feat',
                loadComponent: () => import('app/modules/admin/features/features.component').then(m => m.FeaturesComponent),
                // canActivate: [roleGuard],
            },
            {
                path: 'report',
                loadComponent: () => import('app/modules/admin/daily-report/daily-report.component').then(m => m.DailyReportComponent),
                // canActivate: [roleGuard],
            },
        ],
    },
];
